import { FC, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Mail,
  Phone,
  Calendar,
  Clock,
  Star,
  DollarSign,
  Users,
} from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import {
  TherapistStatusBadge,
  StatusActionModal,
  TherapistStatusHistory,
  ActionType,
} from '../../components/admin/therapist';
import { PermissionGuard } from '../../components/admin/PermissionGuard';
import { useToast } from '../../components/ui/Toast/ToastContext';
import {
  useTherapist,
  useTherapistStatusHistory,
  useApproveTherapist,
  useRejectTherapist,
  useSuspendTherapist,
  useReactivateTherapist,
  getAvailableActions,
} from '../../hooks/admin/useAdminTherapists';
import type { TherapistStatus } from '../../api/adminTherapistApi';

export const TherapistDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  // History pagination
  const [historyPage, setHistoryPage] = useState(1);

  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    action: ActionType;
  }>({
    isOpen: false,
    action: 'approve',
  });

  // Queries
  const { data: therapist, isLoading, error } = useTherapist(id || '');
  const { data: historyData, isLoading: isLoadingHistory } = useTherapistStatusHistory(
    id || '',
    historyPage
  );

  // Mutations
  const approveMutation = useApproveTherapist();
  const rejectMutation = useRejectTherapist();
  const suspendMutation = useSuspendTherapist();
  const reactivateMutation = useReactivateTherapist();

  const isProcessing =
    approveMutation.isPending ||
    rejectMutation.isPending ||
    suspendMutation.isPending ||
    reactivateMutation.isPending;

  const availableActions = therapist ? getAvailableActions(therapist.status as TherapistStatus) : null;

  const openModal = useCallback((action: ActionType) => {
    setModalState({ isOpen: true, action });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({ isOpen: false, action: 'approve' });
  }, []);

  const handleModalConfirm = useCallback(
    async (data: { reason?: string; notes?: string; cancelFutureBookings?: boolean }) => {
      if (!id) return;

      try {
        switch (modalState.action) {
          case 'approve':
            await approveMutation.mutateAsync({ id, notes: data.notes });
            showToast({ type: 'success', message: 'Therapist has been approved' });
            break;
          case 'reject':
            await rejectMutation.mutateAsync({ id, reason: data.reason! });
            showToast({ type: 'success', message: 'Therapist has been rejected' });
            break;
          case 'suspend':
            await suspendMutation.mutateAsync({
              id,
              reason: data.reason!,
              cancelFutureBookings: data.cancelFutureBookings,
            });
            showToast({ type: 'success', message: 'Therapist has been suspended' });
            break;
          case 'reactivate':
            await reactivateMutation.mutateAsync({ id, notes: data.notes });
            showToast({ type: 'success', message: 'Therapist has been reactivated' });
            break;
        }
        closeModal();
      } catch (err) {
        showToast({
          type: 'error',
          message: err instanceof Error ? err.message : `Failed to ${modalState.action} therapist`,
        });
      }
    },
    [id, modalState.action, approveMutation, rejectMutation, suspendMutation, reactivateMutation, showToast, closeModal]
  );

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex h-96 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      </AdminLayout>
    );
  }

  if (error || !therapist) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-red-600 dark:text-red-400">
            {error instanceof Error ? error.message : 'Therapist not found'}
          </p>
          <button
            onClick={() => navigate('/admin/therapists')}
            className="mt-4 text-blue-600 dark:text-blue-400 hover:underline"
          >
            Back to therapists
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <button
              onClick={() => navigate('/admin/therapists')}
              className="mt-1 rounded-lg p-2 text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                {therapist.name}
              </h1>
              <div className="mt-2 flex items-center gap-3">
                <TherapistStatusBadge status={therapist.status as TherapistStatus} />
                {therapist.isAvailable && (
                  <span className="text-sm text-green-600 dark:text-green-400">Available</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <PermissionGuard permission="MANAGE_THERAPISTS">
              <button
                onClick={() => navigate(`/admin/therapists/${id}/edit`)}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700"
              >
                <Edit className="h-4 w-4" />
                Edit
              </button>

              {availableActions?.canApprove && (
                <button
                  onClick={() => openModal('approve')}
                  className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
                >
                  Approve
                </button>
              )}

              {availableActions?.canReject && (
                <button
                  onClick={() => openModal('reject')}
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                >
                  Reject
                </button>
              )}

              {availableActions?.canSuspend && (
                <button
                  onClick={() => openModal('suspend')}
                  className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700"
                >
                  Suspend
                </button>
              )}

              {availableActions?.canReactivate && (
                <button
                  onClick={() => openModal('reactivate')}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  Reactivate
                </button>
              )}
            </PermissionGuard>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - Profile info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Card */}
            <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
              <div className="flex items-start gap-6">
                <div className="h-24 w-24 rounded-full bg-gray-200 dark:bg-slate-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {therapist.photoUrl ? (
                    <img
                      src={therapist.photoUrl}
                      alt={therapist.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-3xl font-medium text-gray-600 dark:text-slate-400">
                      {therapist.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2 text-gray-600 dark:text-slate-400">
                      <Mail className="h-4 w-4" />
                      <span className="text-sm">{therapist.email}</span>
                    </div>
                    {therapist.phone && (
                      <div className="flex items-center gap-2 text-gray-600 dark:text-slate-400">
                        <Phone className="h-4 w-4" />
                        <span className="text-sm">{therapist.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-gray-600 dark:text-slate-400">
                      <Calendar className="h-4 w-4" />
                      <span className="text-sm">
                        Joined {new Date(therapist.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600 dark:text-slate-400">
                      <Clock className="h-4 w-4" />
                      <span className="text-sm">{therapist.yearsOfExperience} years experience</span>
                    </div>
                  </div>
                </div>
              </div>

              {therapist.bio && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">Bio</h3>
                  <p className="text-gray-700 dark:text-slate-300">{therapist.bio}</p>
                </div>
              )}

              {therapist.qualifications.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">
                    Qualifications
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {therapist.qualifications.map((qual) => (
                      <span
                        key={qual}
                        className="rounded-full bg-gray-100 dark:bg-slate-700 px-3 py-1 text-sm text-gray-700 dark:text-slate-300"
                      >
                        {qual}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {therapist.specializations.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">
                    Specializations
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {therapist.specializations.map((spec) => (
                      <span
                        key={spec}
                        className="rounded-full bg-blue-100 dark:bg-blue-900/30 px-3 py-1 text-sm text-blue-700 dark:text-blue-300"
                      >
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Status History */}
            <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-4">
                Status History
              </h2>
              <TherapistStatusHistory
                history={historyData?.data || []}
                pagination={historyData?.pagination}
                isLoading={isLoadingHistory}
                onPageChange={setHistoryPage}
              />
            </div>
          </div>

          {/* Right column - Stats */}
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-4">
                Statistics
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-slate-900">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                      <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <span className="text-gray-600 dark:text-slate-400">Total Bookings</span>
                  </div>
                  <span className="text-xl font-bold text-gray-900 dark:text-slate-100">
                    {therapist.totalReviews || 0}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-slate-900">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
                      <Star className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                    </div>
                    <span className="text-gray-600 dark:text-slate-400">Average Rating</span>
                  </div>
                  <span className="text-xl font-bold text-gray-900 dark:text-slate-100">
                    {therapist.rating ? therapist.rating.toFixed(1) : 'N/A'}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-slate-900">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                      <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <span className="text-gray-600 dark:text-slate-400">Services Offered</span>
                  </div>
                  <span className="text-xl font-bold text-gray-900 dark:text-slate-100">
                    {therapist.services?.length || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Services */}
            {therapist.services && therapist.services.length > 0 && (
              <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-4">
                  Services
                </h2>
                <div className="space-y-2">
                  {therapist.services.map((svc) => (
                    <div
                      key={svc.serviceId}
                      className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-slate-900"
                    >
                      <span className="text-gray-700 dark:text-slate-300">{svc.serviceName}</span>
                      {svc.certifiedAt && (
                        <span className="text-gray-500 dark:text-slate-400 text-sm">
                          Certified {new Date(svc.certifiedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Status Action Modal */}
        {modalState.isOpen && therapist && (
          <StatusActionModal
            isOpen={modalState.isOpen}
            onClose={closeModal}
            onConfirm={handleModalConfirm}
            action={modalState.action}
            therapistName={therapist.name}
            isLoading={isProcessing}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default TherapistDetailPage;
